
import { getAllCourses } from '@/data/courses';
import { normalizeQuizContentEnhanced } from './enhancedQuizNormalizer';

interface QuizValidationResult {
  courseId: string;
  moduleId: string;
  moduleTitle: string;
  hasQuiz: boolean;
  questionCount: number;
  hasDifficultyLevels: boolean;
  hasExplanations: boolean;
  issues: string[];
  status: 'pass' | 'warning' | 'fail';
}

interface QualityAssuranceReport {
  totalModules: number;
  modulesWithQuizzes: number;
  modulesWithIssues: number;
  overallScore: number;
  results: QuizValidationResult[];
  summary: {
    byStatus: { pass: number; warning: number; fail: number };
    byCourse: { [courseId: string]: { total: number; withQuizzes: number } };
  };
}

export function validateAllQuizzes(): QualityAssuranceReport {
  const allCourses = getAllCourses();
  const results: QuizValidationResult[] = [];
  
  const summary = {
    byStatus: { pass: 0, warning: 0, fail: 0 },
    byCourse: {} as { [courseId: string]: { total: number; withQuizzes: number } }
  };

  for (const course of allCourses) {
    summary.byCourse[course.id] = { total: 0, withQuizzes: 0 };
    
    if (!course.modules || course.modules.length === 0) {
      continue;
    }

    for (const module of course.modules) {
      summary.byCourse[course.id].total++;
      
      const result = validateModuleQuiz(course.id, module, course.type);
      results.push(result);
      
      if (result.hasQuiz) {
        summary.byCourse[course.id].withQuizzes++;
      }
      
      summary.byStatus[result.status]++;
    }
  }

  const totalModules = results.length;
  const modulesWithQuizzes = results.filter(r => r.hasQuiz).length;
  const modulesWithIssues = results.filter(r => r.status !== 'pass').length;
  const overallScore = totalModules > 0 ? (modulesWithQuizzes / totalModules) * 100 : 0;

  return {
    totalModules,
    modulesWithQuizzes,
    modulesWithIssues,
    overallScore,
    results,
    summary
  };
}

function validateModuleQuiz(courseId: string, module: any, courseType?: string): QuizValidationResult {
  const issues: string[] = [];
  let status: 'pass' | 'warning' | 'fail' = 'pass';
  
  // Try to normalize the quiz content
  const quizContent = normalizeQuizContentEnhanced(module);
  
  const result: QuizValidationResult = {
    courseId,
    moduleId: module.id,
    moduleTitle: module.title,
    hasQuiz: !!quizContent,
    questionCount: quizContent?.questions.length || 0,
    hasDifficultyLevels: false, // We'll set this based on questions
    hasExplanations: false,
    issues,
    status
  };

  // Check if quiz exists
  if (!quizContent) {
    issues.push('No quiz content available');
    status = 'fail';
    result.status = status;
    return result;
  }

  // Check question count
  if (quizContent.questions.length === 0) {
    issues.push('Quiz has no questions');
    status = 'fail';
  } else if (quizContent.questions.length < 2) {
    issues.push('Quiz has very few questions (less than 2)');
    status = 'warning';
  }

  // Check for explanations
  const questionsWithExplanations = quizContent.questions.filter(q => q.explanation && q.explanation.trim().length > 0);
  result.hasExplanations = questionsWithExplanations.length > 0;
  
  if (questionsWithExplanations.length === 0) {
    issues.push('No questions have explanations');
    if (status === 'pass') status = 'warning';
  } else if (questionsWithExplanations.length < quizContent.questions.length * 0.5) {
    issues.push('Less than 50% of questions have explanations');
    if (status === 'pass') status = 'warning';
  }

  // Check question structure
  for (let i = 0; i < quizContent.questions.length; i++) {
    const question = quizContent.questions[i];
    
    if (!question.question || question.question.trim().length === 0) {
      issues.push(`Question ${i + 1}: Empty question text`);
      status = 'fail';
    }
    
    if (!question.options || question.options.length < 2) {
      issues.push(`Question ${i + 1}: Insufficient answer options`);
      status = 'fail';
    }
    
    if (question.correctAnswer === undefined || question.correctAnswer < 0 || 
        question.correctAnswer >= (question.options?.length || 0)) {
      issues.push(`Question ${i + 1}: Invalid correct answer index`);
      status = 'fail';
    }
    
    if (!question.id) {
      issues.push(`Question ${i + 1}: Missing unique ID`);
      if (status === 'pass') status = 'warning';
    }
  }

  // Check difficulty levels by examining individual questions
  const hasVariedDifficulty = quizContent.questions.some(q => q.difficulty && q.difficulty !== 'medium');
  result.hasDifficultyLevels = hasVariedDifficulty;
  
  if (!hasVariedDifficulty) {
    issues.push('No varied difficulty levels found in questions');
    if (status === 'pass') status = 'warning';
  }

  // Course-specific validation
  if (courseType === 'sql' && courseId.includes('sql')) {
    // SQL-specific validations
    if (!quizContent.questions.some(q => q.category === 'sql' || q.question.toLowerCase().includes('sql'))) {
      issues.push('SQL course quiz should contain SQL-specific questions');
      if (status === 'pass') status = 'warning';
    }
  }

  result.status = status;
  return result;
}

export function generateQualityReport(): string {
  const report = validateAllQuizzes();
  
  let output = '=== QUIZ QUALITY ASSURANCE REPORT ===\n\n';
  
  output += `Overall Statistics:\n`;
  output += `- Total Modules: ${report.totalModules}\n`;
  output += `- Modules with Quizzes: ${report.modulesWithQuizzes}\n`;
  output += `- Modules with Issues: ${report.modulesWithIssues}\n`;
  output += `- Overall Coverage: ${report.overallScore.toFixed(1)}%\n\n`;
  
  output += `Status Breakdown:\n`;
  output += `- Pass: ${report.summary.byStatus.pass}\n`;
  output += `- Warning: ${report.summary.byStatus.warning}\n`;
  output += `- Fail: ${report.summary.byStatus.fail}\n\n`;
  
  output += `Course Breakdown:\n`;
  Object.entries(report.summary.byCourse).forEach(([courseId, stats]) => {
    const coverage = stats.total > 0 ? (stats.withQuizzes / stats.total * 100).toFixed(1) : '0.0';
    output += `- ${courseId}: ${stats.withQuizzes}/${stats.total} (${coverage}%)\n`;
  });
  
  output += `\nDetailed Issues:\n`;
  const issueModules = report.results.filter(r => r.issues.length > 0);
  issueModules.forEach(result => {
    output += `\n${result.courseId}/${result.moduleId} (${result.moduleTitle}):\n`;
    result.issues.forEach(issue => {
      output += `  - ${issue}\n`;
    });
  });
  
  return output;
}

// Export validation functions for testing
export { validateModuleQuiz };
